import { type StripeV2Plan } from './stripeTiers';

/**
 * CAB-602 — the single source of truth for what each subscription plan unlocks.
 *
 * This table lives in CODE, deliberately, not in Firestore:
 *   - It is a product definition that must match the deployed build. A database
 *     row claiming `perioCharting: true` before the module exists would grant
 *     access to nothing and generate support tickets.
 *   - Storing it in the database would not make it more trustworthy. The client
 *     copy is UX only — anyone can edit the bundle in devtools and flip a flag.
 *     What actually protects a capability is the server refusing the call.
 *
 * Per-user state (which plan someone is on, minutes consumed) belongs in the
 * database. What a plan *means* belongs here.
 *
 * Capabilities are named after what the user can do, never after a tier. Call
 * sites ask `can(entitlements, 'clinicalDocuments')` and never compare plan
 * keys, so restricting a different tier later is an edit to this table alone.
 *
 * Source: "Subscription Packages" matrix.
 */

export type PlanKey = StripeV2Plan['key'];

export interface Entitlements {
  /** Number of standard templates available; `null` means all of them. */
  standardTemplates: number | null;
  /** Build and edit custom templates. */
  customTemplates: boolean;
  /** Edit generated charts in-app. */
  chartEdit: boolean;
  /** Referral letters AND consultation reports — one implementation, one gate. */
  clinicalDocuments: boolean;
  /** Hands-free voice control. Not built yet. */
  voiceControl: boolean;
  /** Real-time perio charting module. Not built yet. */
  perioCharting: boolean;
  /** Odontogram support. Not built yet. */
  odontogram: boolean;
  /** Full PMS integration, incl. appointment pull and one-click export. Not built yet. */
  pmsIntegration: boolean;
  /** Cut-and-paste export. Available on every plan. */
  clipboardExport: boolean;
}

/**
 * The boolean capabilities — the ones `can()` answers yes/no for. Numeric
 * allowances (`standardTemplates`) are read off the entitlements object
 * directly, since "how many" is not a yes/no question.
 */
export type Capability = {
  [K in keyof Entitlements]: Entitlements[K] extends boolean ? K : never;
}[keyof Entitlements];

export const ENTITLEMENTS: Record<PlanKey, Entitlements> = {
  free: {
    standardTemplates: 3,
    customTemplates: false,
    chartEdit: false,
    clinicalDocuments: false,
    voiceControl: false,
    perioCharting: false,
    odontogram: false,
    pmsIntegration: false,
    clipboardExport: true,
  },
  basic: {
    standardTemplates: null,
    customTemplates: false,
    chartEdit: true,
    clinicalDocuments: false,
    voiceControl: false,
    perioCharting: false,
    odontogram: false,
    pmsIntegration: false,
    clipboardExport: true,
  },
  pro: {
    standardTemplates: null,
    customTemplates: true,
    chartEdit: true,
    clinicalDocuments: true,
    voiceControl: false,
    perioCharting: false,
    odontogram: false,
    pmsIntegration: false,
    clipboardExport: true,
  },
  ultimate: {
    standardTemplates: null,
    customTemplates: true,
    chartEdit: true,
    clinicalDocuments: true,
    voiceControl: true,
    perioCharting: true,
    odontogram: true,
    pmsIntegration: true,
    clipboardExport: true,
  },
};

/** Cheapest-first plan order. Used to find the lowest plan that unlocks a capability. */
export const PLAN_ORDER: readonly PlanKey[] = ['free', 'basic', 'pro', 'ultimate'];

export const PLAN_LABELS: Record<PlanKey, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  ultimate: 'Ultimate',
};

export function can(entitlements: Entitlements, capability: Capability): boolean {
  return entitlements[capability];
}

/**
 * The cheapest plan that unlocks `capability`, for "upgrade to X to use this"
 * copy. Returns `null` if no plan offers it (which would be a table bug).
 */
export function lowestPlanWith(capability: Capability): PlanKey | null {
  return PLAN_ORDER.find((key) => ENTITLEMENTS[key][capability]) ?? null;
}

/**
 * Resolve a Stripe price id to the plan it belongs to, using the Firestore
 * plans array (`stripe_products/plans`) as the source of truth — the same data
 * the pricing and billing pages read, so the three can never disagree.
 *
 * FAILS CLOSED. An inactive subscription, a missing price id, or a price id
 * that matches no known plan all resolve to `free`. The inverse bug — an
 * unresolvable subscription silently granting Ultimate — is the one that must
 * never happen.
 */
export function resolvePlanKey(
  plans: StripeV2Plan[],
  priceId: string | null | undefined,
  isActive: boolean,
): PlanKey {
  if (!isActive || !priceId) {
    return 'free';
  }
  const match = plans.find((plan) => {
    if (plan.monthlyPriceId === priceId || plan.yearlyPriceId === priceId) {
      return true;
    }
    return (
      plan.userTiers?.some(
        (tier) => tier.monthlyPriceId === priceId || tier.yearlyPriceId === priceId,
      ) === true
    );
  });
  return match?.key ?? 'free';
}

/** Entitlements for a plan key, falling back to Free for anything unrecognised. */
export function entitlementsForPlan(planKey: PlanKey | null | undefined): Entitlements {
  if (!planKey) {
    return ENTITLEMENTS.free;
  }
  return ENTITLEMENTS[planKey] ?? ENTITLEMENTS.free;
}
